import { create } from 'zustand';
import { usersService } from 'src/services/users';
import type { UserResponse, UpdateUserRequest, CreateUserRequest } from 'src/services/users';

interface UsersState {
  items: UserResponse[];
  current: UserResponse | null;
  loading: boolean;
  error: string | null;
  // filters / paging / sorting
  search: string;
  usersOnly: boolean;
  adminsOnly: boolean;
  offset: number;
  limit: number;
  column: 'firstName' | 'lastName' | 'midName' | 'email' | 'role' | 'status' | 'createdAt' | 'updatedAt';
  order: 'asc' | 'desc';
  setSearch: (v: string) => void;
  setUsersOnly: (v: boolean) => void;
  setAdminsOnly: (v: boolean) => void;
  setPage: (page: number) => void;
  setSort: (column: UsersState['column'], order: UsersState['order']) => void;

  loadList: () => Promise<void>;
  loadOne: (id: string | number) => Promise<void>;
  create: (payload: CreateUserRequest) => Promise<UserResponse>;
  update: (id: string | number, payload: UpdateUserRequest) => Promise<UserResponse>;
  remove: (id: string | number) => Promise<void>;
}

export const useUsersStore = create<UsersState>((set, get) => ({
  items: [],
  current: null,
  loading: false,
  error: null,
  search: '',
  usersOnly: false,
  adminsOnly: true,
  offset: 0,
  limit: 12,
  column: 'createdAt',
  order: 'desc',
  setSearch: (v) => set({ search: v, offset: 0 }),
  setUsersOnly: (v) => set({ usersOnly: v, adminsOnly: v ? false : get().adminsOnly, offset: 0 }),
  setAdminsOnly: (v) => set({ adminsOnly: v, usersOnly: v ? false : get().usersOnly, offset: 0 }),
  setPage: (page) => set({ offset: Math.max(0, (page - 1) * get().limit) }),
  setSort: (column, order) => set({ column, order, offset: 0 }),
  loadList: async () => {
    set({ loading: true, error: null });
    try {
      const { usersOnly, adminsOnly, search, offset, limit, column, order } = get();
      const response = await usersService.list({ usersOnly, adminsOnly, search, offset, limit, column, order });
      set({ items: response.data });
    } catch (error: unknown) {
      set({ error: (error as { message?: string })?.message || 'Failed to load users' });
      throw error;
    } finally {
      set({ loading: false });
    }
  },
  loadOne: async (id) => {
    set({ loading: true, error: null });
    try {
      const data = await usersService.getOne(id);
      set({ current: data });
    } catch (error: unknown) {
      set({ error: (error as { message?: string })?.message || 'Failed to load user' });
      throw error;
    } finally {
      set({ loading: false });
    }
  },
  create: async (payload) => {
    set({ loading: true, error: null });
    try {
      const created = await usersService.create(payload);
      set({ items: [created, ...get().items] });
      return created;
    } catch (error: unknown) {
      set({ error: (error as { message?: string })?.message || 'Failed to create user' });
      throw error;
    } finally {
      set({ loading: false });
    }
  },
  update: async (id, payload) => {
    set({ loading: true, error: null });
    try {
      const updated = await usersService.update(id, payload);
      set({
        current: get().current && (get().current as UserResponse).id === updated.id ? updated : get().current,
        items: get().items.map((u) => (u.id === updated.id ? updated : u)),
      });
      return updated;
    } catch (error: unknown) {
      set({ error: (error as { message?: string })?.message || 'Failed to update user' });
      throw error;
    } finally {
      set({ loading: false });
    }
  },
  remove: async (id) => {
    set({ loading: true, error: null });
    try {
      await usersService.remove(id);
      set({
        items: get().items.filter((u) => u.id !== id),
        current: get().current && (get().current as UserResponse).id === id ? null : get().current,
      });
    } catch (error: unknown) {
      set({ error: (error as { message?: string })?.message || 'Failed to delete user' });
      throw error;
    } finally {
      set({ loading: false });
    }
  },
}));


